import React from 'react';
import { TitleText, BodyText, BodyTextSmall } from './common';
import { ContinueWithAppleButton } from '../components/ContinueWithAppleButton';
import { ContinueWithEmailButton } from '../components/ContinueWithEmailButton';
import { ContinueWithFacebookButton } from '../components/ContinueWithFacebookButton';
import { ContinueWithGoogleButton } from '../components/ContinueWithGoogleButton';
import routes from 'js/core_pre_webpack/routes';
import { Options as ClickTrackingOptions } from 'js/utils/trackClick';
import styled from 'astroturf/react'
import { VerticalSpacer } from '../components/VerticalSpacer';
import { ModalError } from '../components/ModalError';
import {
  ButtonsPageConfigurableText,
  RegistrationParams,
  ModalPage,
} from '../types';
import { AttachmentThumbnail } from '../components/AttachmentThumbnail';
import _ButtonStyleContext from "ButtonsPage-ButtonStyleContext.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/login_modal/pages/ButtonsPage.tsx?styleId=ButtonStyleContext";
import _SharedButtonStyles from "ButtonsPage-SharedButtonStyles.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/login_modal/pages/ButtonsPage.tsx?styleId=SharedButtonStyles";
import _StyledTitleText from "ButtonsPage-StyledTitleText.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/login_modal/pages/ButtonsPage.tsx?styleId=StyledTitleText";
import _StyledBodyText from "ButtonsPage-StyledBodyText.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/login_modal/pages/ButtonsPage.tsx?styleId=StyledBodyText";


type Props = {
  onAttempt?: () => void;
  onAuthorized?: () => void;
  onSuccess?: (redirect?: string) => void;
  onError: (error?: { errorMessage?: string; errorCodes?: string[] }) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  errorMessage?: string;
  onEmailButtonClick: () => void;
  show: boolean;
  clickTrackingContext?: Partial<ClickTrackingOptions>;
  text: ButtonsPageConfigurableText;
  registrationParams: RegistrationParams;
  setPage: React.Dispatch<React.SetStateAction<ModalPage>>;
  showAttachmentThumbnail?: boolean;
};

const ButtonStyleContext = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonStyleContext",
  styles: _ButtonStyleContext
});

const SharedButtonStyles = _SharedButtonStyles;

const StyledTitleText = /*#__PURE__*/styled(TitleText, null, {
  displayName: "StyledTitleText",
  styles: _StyledTitleText
});

const StyledBodyText = /*#__PURE__*/styled(BodyText, null, {
  displayName: "StyledBodyText",
  styles: _StyledBodyText
});

export const ButtonsPage: React.FC<Props> = (props) => {
  const {
    onAttempt,
    onSuccess,
    onError,
    loading,
    setLoading,
    errorMessage,
    onEmailButtonClick,
    show,
    clickTrackingContext,
    text,
    registrationParams,
    setPage,
    showAttachmentThumbnail,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userInChina = (window as any).userInChina === 'true';

  // Feature flag for disabling Facebook login - hardcoded to true for testing
  const facebookLoginDisabled =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).facebookLoginDisabled === 'true';

  const handleFacebookButtonClick = () => {
    if (facebookLoginDisabled) {
      // Navigate to the facebook-disabled page
      setPage('facebook-disabled');
    }
    // If not disabled, the ContinueWithFacebookButton handles its own click logic
  };

  const shouldShowThumbnail =
    showAttachmentThumbnail &&
    Boolean(window.loginModal?.attachmentThumbnailUrl);

  return (
    <ButtonStyleContext show={show}>
      {shouldShowThumbnail && (
        <AttachmentThumbnail
          attachmentThumbnailUrl={window.loginModal.attachmentThumbnailUrl!}
        />
      )}
      <StyledTitleText
        $withThumbnail={shouldShowThumbnail}
        className={shouldShowThumbnail ? 'withThumbnail' : ''}
      >
        {text.header}
      </StyledTitleText>
      <StyledBodyText
        $withBorder={shouldShowThumbnail}
        className={shouldShowThumbnail ? 'withBorder' : ''}
      >
        {text.subheader}
      </StyledBodyText>
      <ModalError errorMessage={errorMessage} show={Boolean(errorMessage)} />
      <VerticalSpacer height={32} />
      <BodyTextSmall>
        By clicking Continue, you agree to our{' '}
        <a href={routes.terms_path()}>Terms of Use</a> and{' '}
        <a href={routes.privacy_path()}>Privacy Policy</a>
      </BodyTextSmall>
      <VerticalSpacer height={16} />
      {!userInChina && (
        <ContinueWithGoogleButton
          onAttempt={onAttempt}
          onError={onError}
          onSuccess={onSuccess}
          disabled={loading}
          className={`${SharedButtonStyles.margins} ${shouldShowThumbnail ? SharedButtonStyles.compactMargins : ''}`}
          setLoading={setLoading}
          clickTrackingContext={clickTrackingContext}
          buttonText={text.googleButton}
          registrationParams={registrationParams}
          {...(shouldShowThumbnail && { size: 'sm' as const })}
        />
      )}
      <ContinueWithAppleButton
        onAttempt={onAttempt}
        onError={onError}
        onSuccess={onSuccess}
        disabled={loading}
        className={`${SharedButtonStyles.margins} ${shouldShowThumbnail ? SharedButtonStyles.compactMargins : ''}`}
        setLoading={setLoading}
        clickTrackingContext={clickTrackingContext}
        buttonText={text.appleButton}
        registrationParams={registrationParams}
        {...(shouldShowThumbnail && { size: 'sm' as const })}
      />
      {!userInChina &&
        (facebookLoginDisabled ? (
          // When Facebook login is disabled, render a button that looks the same but navigates to the disabled page
          <ContinueWithFacebookButton
            onClick={handleFacebookButtonClick}
            onError={onError}
            onSuccess={onSuccess}
            disabled={loading}
            className={`${SharedButtonStyles.margins} ${shouldShowThumbnail ? SharedButtonStyles.compactMargins : ''}`}
            setLoading={setLoading}
            clickTrackingContext={clickTrackingContext}
            buttonText={text.facebookButton}
            registrationParams={registrationParams}
            {...(shouldShowThumbnail && { size: 'sm' as const })}
          />
        ) : (
          // Normal Facebook OAuth flow
          <ContinueWithFacebookButton
            onAttempt={onAttempt}
            onError={onError}
            onSuccess={onSuccess}
            disabled={loading}
            className={`${SharedButtonStyles.margins} ${shouldShowThumbnail ? SharedButtonStyles.compactMargins : ''}`}
            setLoading={setLoading}
            clickTrackingContext={clickTrackingContext}
            buttonText={text.facebookButton}
            registrationParams={registrationParams}
            {...(shouldShowThumbnail && { size: 'sm' as const })}
          />
        ))}
      <ContinueWithEmailButton
        onClick={onEmailButtonClick}
        disabled={loading}
        className={`${SharedButtonStyles.margins} ${shouldShowThumbnail ? SharedButtonStyles.compactMargins : ''}`}
        buttonText={text.emailButton}
        {...(shouldShowThumbnail && { size: 'sm' as const })}
      />
    </ButtonStyleContext>
  );
};
